import { useCallback, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  CorsService,
  type AllowedOriginAddPayload,
  type AllowedOriginConflictDetail,
  type AllowedOriginsResponse,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n/types";
import { ApiError } from "@/api";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Skeleton } from "@/components/ui/skeleton";
import { Section } from "@/components/shared/section";

const TENANT_ADMIN_ROLES = new Set(["admin", "super_admin"]);

type OriginErrorCode =
  | "empty"
  | "wildcard"
  | "whitespace"
  | "format"
  | "scheme"
  | "path"
  | "query"
  | "userinfo"
  | "https";

const ERROR_KEY: Record<OriginErrorCode, TranslationKey> = {
  empty: "settings.allowedOrigins.errors.empty",
  wildcard: "settings.allowedOrigins.errors.wildcard",
  whitespace: "settings.allowedOrigins.errors.whitespace",
  format: "settings.allowedOrigins.errors.format",
  scheme: "settings.allowedOrigins.errors.scheme",
  path: "settings.allowedOrigins.errors.path",
  query: "settings.allowedOrigins.errors.query",
  userinfo: "settings.allowedOrigins.errors.userinfo",
  https: "settings.allowedOrigins.errors.https",
};

/**
 * Lightweight FE mirror of the backend ``normalize_origin``. Catches
 * obvious mistakes (missing scheme, trailing path, wildcards) before
 * a round-trip; the BE is still authoritative.
 */
function validateOriginShape(
  raw: string,
  isProduction: boolean
): OriginErrorCode | null {
  const trimmed = raw.trim();
  if (!trimmed) return "empty";
  if (trimmed.includes("*")) return "wildcard";
  if (trimmed.includes(" ") || /[\n\r]/.test(trimmed)) return "whitespace";
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return "format";
  }
  if (!["http:", "https:"].includes(url.protocol)) return "scheme";
  if (url.pathname && url.pathname !== "/" && url.pathname !== "")
    return "path";
  if (url.search || url.hash) return "query";
  if (url.username || url.password) return "userinfo";
  const isLocal = ["localhost", "127.0.0.1", "[::1]"].includes(url.hostname);
  if (isProduction && url.protocol !== "https:" && !isLocal) return "https";
  return null;
}

export default function SettingsAllowedOriginsTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const canEdit = user ? TENANT_ADMIN_ROLES.has(user.role) : false;

  const fetcher = useCallback(() => CorsService.list(), []);
  const { data, error, isLoading, refetch } =
    useApi<AllowedOriginsResponse>(fetcher);

  const [draft, setDraft] = useState("");
  const [conflict, setConflict] = useState<AllowedOriginConflictDetail | null>(
    null
  );

  // Heuristic: window.location.protocol === https → assume the
  // operator is on a real deploy. Dev (Vite proxy) uses http.
  const isProduction = useMemo(
    () =>
      typeof window !== "undefined" && window.location.protocol === "https:",
    []
  );

  const draftError = useMemo(() => {
    if (!draft) return null;
    const code = validateOriginShape(draft, isProduction);
    if (!code) return null;
    return t(ERROR_KEY[code]);
  }, [draft, isProduction, t]);

  const existingOrigins = data?.origins ?? [];
  const isDuplicate = useMemo(() => {
    if (!draft) return false;
    const normalised = draft.trim().toLowerCase().replace(/\/+$/, "");
    return existingOrigins.some(
      (o) => o.toLowerCase() === normalised
    );
  }, [draft, existingOrigins]);

  const addMutation = useMutation<
    AllowedOriginAddPayload,
    AllowedOriginsResponse
  >((payload) => CorsService.add(payload), {
    onSuccess: () => {
      setDraft("");
      setConflict(null);
      void refetch();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409 && err.payload) {
        const payload = err.payload as { detail?: AllowedOriginConflictDetail };
        if (payload.detail && typeof payload.detail === "object") {
          setConflict(payload.detail);
          return;
        }
      }
      setConflict(null);
    },
  });

  const removeMutation = useMutation<
    AllowedOriginAddPayload,
    AllowedOriginsResponse
  >((payload) => CorsService.remove(payload), {
    onSuccess: () => {
      void refetch();
    },
  });

  const canSubmit =
    canEdit && draft.trim().length > 0 && !draftError && !isDuplicate;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setConflict(null);
    await addMutation.mutate({ origin: draft.trim() });
  };

  const handleRemove = async (origin: string) => {
    if (!canEdit) return;
    if (
      typeof window !== "undefined" &&
      !window.confirm(
        t("settings.allowedOrigins.removeConfirm", { origin })
      )
    ) {
      return;
    }
    await removeMutation.mutate({ origin });
  };

  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error && !data) {
    return <Alert variant="warning">{parseApiError(error)}</Alert>;
  }

  return (
    <div className="space-y-8">
      <Alert variant="info">{t("settings.allowedOrigins.intro")}</Alert>
      {!canEdit && (
        <Alert variant="info">
          {t("settings.allowedOrigins.readOnlyNotice")}
        </Alert>
      )}
      {addMutation.error && !conflict && (
        <Alert variant="danger">{parseApiError(addMutation.error)}</Alert>
      )}
      {conflict && (
        <Alert variant="danger" role="alert">
          {t("settings.allowedOrigins.conflictMessage", {
            origin: conflict.origin,
          })}
        </Alert>
      )}
      {removeMutation.error && (
        <Alert variant="danger">{parseApiError(removeMutation.error)}</Alert>
      )}

      <Section
        title={t("settings.allowedOrigins.section.title")}
        description={t("settings.allowedOrigins.section.description")}
      >
        {canEdit && (
          <form
            onSubmit={handleSubmit}
            className="space-y-3 rounded-lg border border-ocean-100 bg-white p-5"
          >
            <FormField
              label={t("settings.allowedOrigins.section.inputLabel")}
              value={draft}
              onChange={setDraft}
              placeholder={t(
                "settings.allowedOrigins.section.inputPlaceholder"
              )}
              maxLength={255}
              error={
                draftError
                  ? draftError
                  : isDuplicate
                    ? t("settings.allowedOrigins.errors.duplicate")
                    : undefined
              }
              hint={t("settings.allowedOrigins.section.inputHint")}
            />
            <div className="flex items-center justify-end">
              <Button
                type="submit"
                disabled={addMutation.isLoading || !canSubmit}
              >
                <Plus size={14} />
                {addMutation.isLoading
                  ? t("settings.allowedOrigins.section.addButtonLoading")
                  : t("settings.allowedOrigins.section.addButton")}
              </Button>
            </div>
          </form>
        )}

        <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
          {existingOrigins.length === 0 ? (
            <p className="p-5 text-sm text-neutral-600">
              {t("settings.allowedOrigins.empty")}
            </p>
          ) : (
            <ul className="divide-y divide-ocean-100">
              {existingOrigins.map((origin) => (
                <li
                  key={origin}
                  className="flex items-center justify-between gap-3 p-4"
                >
                  <code className="break-all text-sm text-ocean-900">
                    {origin}
                  </code>
                  {canEdit && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemove(origin)}
                      disabled={removeMutation.isLoading}
                      aria-label={t(
                        "settings.allowedOrigins.removeAria",
                        { origin }
                      )}
                    >
                      <Trash2 size={14} />
                      {t("settings.allowedOrigins.removeButton")}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>
    </div>
  );
}
